import styled, { css } from "styled-components";
import {
  boundaries,
  fromInputDate,
  intoInputDate,
  parseCSS,
  parseDate,
  parseID,
  parseNumber,
} from "scripts/FunctionsBundle";
import { useCallback, useEffect, useState } from "react";

// ################################ INTERFACES & PROPS ################################
type _Base = import("@utils/ClassTypes")._Base;
// SearchTable => Rename all instances to use
type SearchTableProps = {
  /** Nombre de la colección de datos para mostrar en caso de no haya registros. Ejemplo: `"bautizos"`. */
  _collection_title?: string;
  /** Colección de datos, este es el arreglo de objetos a colocar en la tabla. */
  _collection: any[];
  /** Mapa para los encabezados, si no existe se usa el nombre de la propiedad tal cual. Un mapa como `"aanyLabels"`, primero la propiedad, luego el nombre. */
  _headers?: Map<string, string>;

  /** Lista con los valores a representar según si una propiedad es boolean (false y true). Ejemplo: `"sexo": ["hombre", "mujer"]` */
  _booleans?: { [x: string]: [string, string] };
  /** Indica si implementar un módulo para búsquedas usando `<SearchModal >` */
  _allowSearch?: boolean;
  /** Indica si permitir el ordenamiento por columnas al pulsar sombre un encabezado. */
  _allowOrder?: boolean;
  /** Indica si permitir que se oculten las columnas al pulsar clic derecho sobre ellas. */
  _allowCollapse?: boolean;

  /** Indica qué debería suceder cuando se pulsa sobre un elemento de la tabla (una fila). */
  _onSelect?: (el: any, currID: string) => void;
} & _Base;

// ################################ RENDERING COMPONENT ################################
const _SearchTable = (props: SearchTableProps) => {
  const [showList, setShowList] = useState<React.JSX.Element[]>([]);
  const [pagList, setPagList] = useState<React.JSX.Element[]>([]);
  const [pag, setPag] = useState(0);

  // const [limpiar, volkey] = useRefresh();
  const [currentID, setCurrentID] = useState<string>("");
  const [currentLbl, setCurrentLbl] = useState("id");
  const [sortedList, setSortedList] = useState<object[]>([]);
  const [LS, setLS] = useState({
    sort: {
      label: "id",
      asc: true,
    },
    hide: [] as string[],
    search: {} as object,
  });

  // ------------------------------------------------------------------------------------ MAP LABELS
  const mapLabels = useCallback(
    (el: any, head?: boolean): React.JSX.Element[] => {
      const { update, _specifics, id, ...rest } = el;
      const ells: React.JSX.Element[] = [];

      function temp(v: string, lbl: string) {
        if (rest[lbl] === undefined) return;

        const cls = LS.hide.includes(lbl) ? "col-hidden" : "";
        if (head) {
          ells.push(
            <th
              key={v}
              className={cls + (currentLbl === lbl ? " col-sel" : "")}
              onClick={() => setCurrentLbl(lbl)}
            >
              <div className="text-container">
                {v + (LS.search[lbl] ? " ?" : "")}
              </div>
            </th>
          );
        } else {
          let val = rest[lbl] ?? "NULL";
          if (typeof val === "boolean") {
            val = val
              ? props._booleans?.[lbl]?.[1] || "Verdadero"
              : props._booleans?.[lbl]?.[0] || "Falso";
          } else if (val instanceof Date) {
            val = parseDate(val);
          } else if (val === "") {
            val = "N/D";
          }

          ells.push(
            <td key={lbl + "-" + val} className={cls}>
              <div className="text-container">{val}</div>
            </td>
          );
        }
      }

      // Primero siempre el ID
      const c = LS.hide.includes("id") ? "col-hidden" : "";
      ells.push(
        head ? (
          <th
            key={"id"}
            className={c + (currentLbl === "id" ? " col-sel" : "")}
            onClick={() => setCurrentLbl("id")}
          >
            <div className="text-container">
              {"ID" + (LS.search["id"] ? " ?" : "")}
            </div>
          </th>
        ) : (
          <td key={el.id} className={c}>
            <div className="text-container">{el.id ?? "NULL"}</div>
          </td>
        )
      );

      // Luego los demás elementos
      if (props._headers !== undefined) props._headers.forEach(temp);
      else for (const key in rest) temp(key, key);

      return ells;
    },
    [LS.hide, LS.search, currentLbl, props._booleans, props._headers]
  );

  // --------------------------------------------------------------------------########## SORTED LIST FOR-LOOP
  useEffect(() => {
    const listing = structuredClone(props._collection).sort((a, b) => {
      const AA = a[LS.sort.label] as string | number | Date | boolean | null;
      const BB = b[LS.sort.label];
      const r = LS.sort.asc ? 1 : -1;
      if (typeof AA === "string") return AA.localeCompare(BB) * r;
      if (
        typeof AA === "boolean" ||
        typeof AA === "number" ||
        AA instanceof Date
      )
        return (AA > BB ? 1 : -1) * r;
      return 0;
    });

    setSortedList(listing);
  }, [LS.sort.asc, LS.sort.label, props._collection]);

  // --------------------------------------------------------------------------########## SHOW TABLE FOR-LOOP
  useEffect(() => {
    let c = 0;
    const tlist: React.JSX.Element[] = [];

    main: for (let i = 0; i < sortedList.length; i++) {
      const el = sortedList[i] as any;

      // ------------------------------------------ FILTER
      for (const p in LS.search) {
        const fil = LS.search[p] as
          | { min?: number; max?: number }
          | string
          | boolean;

        if (el[p] === undefined || fil === "" || fil === undefined) continue;

        if (typeof fil === "string") {
          if (!parseID(el[p]).includes(parseID(fil))) continue main;
        } else if (typeof fil === "boolean") {
          if (fil !== el[p]) continue main;
        } else {
          if (fil.min && fil.min >= el[p]) continue main;
          if (fil.max && fil.max <= el[p]) continue main;
        }
      }

      // ------------------------------------------ GUARDAR ELEMENTO
      tlist.push(
        <tr
          key={el.id + "-" + i}
          className={"item " + (currentID === el.id ? "sel" : "")}
          onClick={() => {
            setCurrentID(el.id || "");
            props._onSelect?.(el, currentID);
          }}
        >
          <td>
            <div className="text-container">#{++c}.-</div>
          </td>
          {mapLabels(el)}
        </tr>
      );
    }

    setShowList(tlist);
  }, [LS.search, currentID, mapLabels, props, sortedList]);

  // --------------------------------------------------------------------------########## SHOW BUTTONS FOR-LOOP
  useEffect(() => {
    const aux: React.JSX.Element[] = [];
    for (let i = 0; i < showList.length / 10; i++) {
      aux.push(
        <button key={i} disabled={pag === i} onClick={() => setPag(i)}>
          {i * 10 + 1 + " - " + (i * 10 + 10)}
        </button>
      );
    }
    setPagList(aux);
  }, [pag, showList]);

  // ------------------------------------------------------------------------------------ HANDLE ---- CLEAR
  function handleFilter(type = 0) {
    if (type === 1) LS.search[currentLbl] = "";
    else if (type === 2) LS.search = {};

    setPag(0);
    setLS({ ...LS, search: { ...LS.search } }); // SI ACTUALIZA EN TIEMPO REAL
  }

  // ------------------------------------------------------------------------------------ HANDLE ---- CHANGE
  function handleChange(val: string | boolean | undefined) {
    LS.search[currentLbl] = val;
    setLS({ ...LS }); // NO ACTUALIZA EN TIEMPO REAL
  }

  // ------------------------------------------------------------------------------------ HANDLE ---- RANGE CHANGE
  function handleRangeChange(val: string, isMax?: boolean, isDate?: boolean) {
    const t = isDate ? fromInputDate(val) : parseNumber(val);

    LS.search[currentLbl] = {
      min: !isMax ? t : LS.search[currentLbl]?.min,
      max: isMax ? t : LS.search[currentLbl]?.max,
    };

    setLS({ ...LS }); // NO ACTUALIZA EN TIEMPO REAL
  }

  // ------------------------------------------------------------------------------------ HANDLE ---- SORT
  function handleSort(desc?: boolean) {
    setLS({ ...LS, sort: { label: currentLbl, asc: !desc } });
  }

  // ------------------------------------------------------------------------------------ HANDLE ---- HIDE
  function handleHide(all?: boolean) {
    if (all) LS.hide = [];
    else LS.hide.push(currentLbl);
    setLS({ ...LS, hide: [...LS.hide] });
  }

  // ------------------------------------------------------------------------------------ RETURN
  if (!props._collection || !props._collection[0]) {
    return (
      <div className={props.className}>
        <h2>
          {'No existe ningún registro en "' + props._collection_title + '".'}
        </h2>
      </div>
    );
  }

  return (
    <div className={props.className}>
      {/* ------------------------------------------------------------------------------------ CONTROLS */}
      <div className="controls-container">
        {/* ------------------------------------------------------------------------------------ FILTER */}
        <div className="controls-filter">
          <h3>Filtrar</h3>
          <div className="controls-filter-input">
            <label htmlFor="input-filter-text">
              {props._headers?.get(currentLbl) || "ID"}
            </label>

            {typeof props._collection[0][currentLbl] === "boolean" ? (
              // ------------------------------------------ BOOLEAN
              <>
                <label>
                  <input
                    type="radio"
                    id="input-filter-bool"
                    name="filter-boolean"
                    defaultChecked={LS.search[currentLbl] === undefined}
                    onClick={() => handleChange(undefined)}
                  />
                  Cualquiera
                </label>

                <label>
                  <input
                    type="radio"
                    name="filter-boolean"
                    defaultChecked={LS.search[currentLbl] === false}
                    onClick={() => handleChange(false)}
                  />
                  {props._booleans?.[currentLbl]?.[0] || "Falso"}
                </label>

                <label>
                  <input
                    type="radio"
                    name="filter-boolean"
                    defaultChecked={LS.search[currentLbl] === true}
                    onClick={() => handleChange(true)}
                  />
                  {props._booleans?.[currentLbl]?.[1] || "Verdadero"}
                </label>
              </>
            ) : props._collection[0][currentLbl] instanceof Date ? (
              // ------------------------------------------ DATE RANGE
              <>
                <label htmlFor="fil-date-min">desde</label>
                <input
                  id="fil-date-min"
                  type={"date"}
                  value={
                    LS.search[currentLbl]?.min
                      ? intoInputDate(LS.search[currentLbl]?.min)
                      : ""
                  }
                  onChange={(e) => {
                    handleRangeChange(e.target.value, false, true);
                  }}
                />

                <label htmlFor="fil-date-max">hasta</label>
                <input
                  id="fil-date-max"
                  type={"date"}
                  value={
                    LS.search[currentLbl]?.max
                      ? intoInputDate(LS.search[currentLbl]?.max)
                      : ""
                  }
                  onChange={(e) => {
                    handleRangeChange(e.target.value, true, true);
                  }}
                />
              </>
            ) : typeof props._collection[0][currentLbl] === "number" ? (
              // ------------------------------------------ NUMBER RANGE
              <>
                <label htmlFor="fil-num-min">entre</label>
                <input
                  id="fil-num-min"
                  type={"number"}
                  min={0}
                  defaultValue={LS.search[currentLbl]?.min ?? ""}
                  onChange={(e) => {
                    handleRangeChange(e.target.value);
                  }}
                />

                <label htmlFor="fil-num-max">y</label>
                <input
                  id="fil-num-max"
                  type={"number"}
                  min={0}
                  defaultValue={LS.search[currentLbl]?.max ?? ""}
                  onChange={(e) => {
                    handleRangeChange(e.target.value, true);
                  }}
                />
              </>
            ) : (
              // ------------------------------------------ STRING FIELD
              <input
                type="search"
                id="input-filter-text"
                value={LS.search[currentLbl] || ""}
                onChange={(e) => handleChange(e.target.value)}
                style={{ flexGrow: 1 }}
              />
            )}
          </div>

          {/* ------------------------------------------ BUTTONS */}
          <button className="primary" onClick={() => handleFilter(0)}>
            Buscar
          </button>

          <button className="secondary" onClick={() => handleFilter(1)}>
            Limpiar filtro
          </button>

          <button className="danger" onClick={() => handleFilter(2)}>
            Quitar todos los filtros
          </button>
        </div>

        {/* ------------------------------------------------------------------------------------ SORT */}
        <div className="controls-sort">
          <h3>Ordenar</h3>

          <button
            disabled={LS.sort.label === currentLbl && LS.sort.asc}
            onClick={() => handleSort()}
          >
            &#xF571;
          </button>

          <button
            disabled={LS.sort.label === currentLbl && !LS.sort.asc}
            onClick={() => handleSort(true)}
          >
            &#xF573;
          </button>
        </div>

        {/* ------------------------------------------------------------------------------------ HIDE */}
        <div className="controls-hide">
          <h3>Visibilidad</h3>

          <button onClick={() => handleHide()}>Ocultar</button>

          <button disabled={!LS.hide.length} onClick={() => handleHide(true)}>
            Revelar todos
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------------------------------ TABLE */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>
                <div className="text-container">No.#</div>
              </th>
              {mapLabels(props._collection[0] || {}, true)}
            </tr>
          </thead>

          <tbody>{showList.slice(pag * 10, (pag + 1) * 10)}</tbody>
        </table>

        {/* ------------------------------------------ BACKGROUND FILLER */}
        <div className="bg-filler" />
      </div>

      {/* ------------------------------------------------------------------------------------ PAGINATION */}
      <div className="pagination">
        <button
          disabled={pag === 0}
          onClick={() =>
            setPag((v) =>
              boundaries(v - 1, 0, Math.floor((showList.length - 1) / 10))
            )
          }
        >
          {"<"}
        </button>

        <div className="pagination-buttons-wrapper">
          <div className={"pagination-buttons"}>{pagList}</div>
        </div>

        <button
          disabled={pag + 1 > (showList.length - 1) / 10}
          onClick={() =>
            setPag((v) =>
              boundaries(v + 1, 0, Math.floor((showList.length - 1) / 10))
            )
          }
        >
          {">"}
        </button>
      </div>
    </div>
  );
};

// ################################ STYLES ################################
const SearchTable = styled(_SearchTable).attrs(
  (props: SearchTableProps): SearchTableProps => {
    return {
      _collection_title: "colección",
      ...props,
    };
  }
)`
  ${(props) => css`
    display: block;
    width: 90%;
    margin: 0.5rem auto;

    .controls-container {
      display: grid;
      grid-template-columns: 3fr 1fr 1fr;

      .controls-filter-input,
      h3 {
        display: flex;
        grid-column: 1/-1;
        align-items: center;
      }

      button {
        font-size: 1rem;
        margin-left: 0;
        margin-right: 0;
      }

      .controls-filter,
      .controls-sort,
      .controls-hide {
        display: grid;
        padding: 0 1.5em;
        grid-column-gap: 1rem;
      }

      .controls-filter {
        grid-template-columns: 1fr 1fr 1fr;
      }

      .controls-sort {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 2fr;

        button {
          font-family: var(--font-icons);
          font-size: 3rem;
          padding: 0;
        }
      }
    }

    .pagination {
      display: flex;
      align-items: flex-start;

      button {
        margin-left: 0;
        margin-right: 0;
        white-space: nowrap;
      }
    }

    .pagination-buttons-wrapper {
      flex-grow: 1;
      margin: 0 1rem;
      overflow: auto hidden;
      text-align: center;
    }

    .pagination-buttons {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      flex-wrap: nowrap;

      flex-grow: 1;
      margin: 0 1rem;
      column-gap: 1rem;
    }

    ${parseCSS(props._style)}
  `}
`;

// ################################ EXPORTS ################################
export default SearchTable;
